/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| 
 | |_| | | | | |_) || |  / / | | |  \| | | | | || | 
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___|
                                                                                                                                                                                                                                                                                                                                       
=========================================================
* Horizon UI - v1.1.0
=========================================================

* Product Page: https://www.horizon-ui.com/
* Copyright 2023 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Chakra imports
import {
  Avatar,
  Box,
  Heading,
  Flex,
  Text,
  FormLabel,
  Icon,
  Select,
  SimpleGrid,
  useColorModeValue,
} from "@chakra-ui/react";
// Assets
import Usa from "assets/img/dashboards/usa.png";
// Custom components
import MiniCalendar from "components/calendar/MiniCalendar";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React from "react";
import {
  MdAddTask,
  MdAttachMoney,
  MdBarChart,
  MdFileCopy,
  MdAppRegistration,
  MdOutlineAccountTree,
  MdOutlinePassword,
  MdOutlineWater

} from "react-icons/md";
import { FcBiotech } from "react-icons/fc";
import { PiFilesBold } from "react-icons/pi";
import CheckTable from "views/admin/default/components/CheckTable";
import ComplexTable from "views/admin/default/components/ComplexTable";
import DailyTraffic from "views/admin/default/components/DailyTraffic";
import PieCard from "views/admin/default/components/PieCard";
import CountCard from "views/admin/default/components/CountCard";
import TopGlycansCard from "views/admin/default/components/TopGlycansCard";
import Tasks from "views/admin/default/components/Tasks";
import TotalSpent from "views/admin/default/components/TotalSpent";
import WeeklyRevenue from "views/admin/default/components/WeeklyRevenue";
import {
  columnsDataCheck,
  columnsDataComplex,
} from "views/admin/default/variables/columnsData";
import tableDataCheck from "views/admin/default/variables/tableDataCheck.json";
import tableDataComplex from "views/admin/default/variables/tableDataComplex.json";

import { useEffect, useState } from "react";
import { API, graphqlOperation } from 'aws-amplify';
import { listGlycopeptideatlasProjectLists } from "graphql/queries";

export default function UserReports() {
  // Chakra Color Mode
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

  // const [dataCount, setDataCount] = useState(0);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       const result = await API.graphql(graphqlOperation(listGlycopeptideatlasProjectLists));
  //       const items = result.data.listGlycopeptideatlasProjectLists.items;
  //       setDataCount(items.length);
  //     } catch (error) {
  //       console.error('Error fetching data: ', error);
  //     }
  //   };

  //   fetchData();
  // }, []);

  const [projectsCount, setProjectsCount] = useState(null);

  useEffect(() => {
    fetch('http://glycopeptideatlas-eb-env.eba-zxebtsmp.us-west-2.elasticbeanstalk.com/api/get-projects-count/')
    // fetch('http://localhost:8000/api/get-projects-count/')
      .then(response => response.json())
      .then(data => {
        setProjectsCount(data.count);  // Assuming data is the value you want to display
      })
      .catch(error => console.error('Failed to fetch data:', error));
  }, []);


  const [filesCount, setFilesCount] = useState(null);

  useEffect(() => {
    fetch('http://glycopeptideatlas-eb-env.eba-zxebtsmp.us-west-2.elasticbeanstalk.com/api/get-files-count/')
    // fetch('http://localhost:8000/api/get-files-count/')
      .then(response => response.json())
      .then(data => {
        setFilesCount(data.count);  // Assuming data is the value you want to display
      })
      .catch(error => console.error('Failed to fetch data:', error));
  }, []);

  const [proteinsCount, setProteinsCount] = useState(null);

  useEffect(() => {
    fetch('http://glycopeptideatlas-eb-env.eba-zxebtsmp.us-west-2.elasticbeanstalk.com/api/get-proteins-count/')
    // fetch('http://localhost:8000/api/get-proteins-count/')
      .then(response => response.json())
      .then(data => {
        setProteinsCount(data.count);  // Assuming data is the value you want to display
      })
      .catch(error => console.error('Failed to fetch data:', error));
  }, []);

  const [nglycopeptidesCount, setNGlycopeptidesCount] = useState(null);
  useEffect(() => {
    fetch('http://glycopeptideatlas-eb-env.eba-zxebtsmp.us-west-2.elasticbeanstalk.com/api/get-nglycopeptides-count/')
    // fetch('http://localhost:8000/api/get-nglycopeptides-count/')
      .then(response => response.json())
      .then(data => {
        setNGlycopeptidesCount(data.count);  // Assuming data is the value you want to display
      })
      .catch(error => console.error('Failed to fetch data:', error));
  }, []);

  const [nGlycansCount, setNGlycansCount] = useState(null);
  useEffect(() => {
    fetch('https://glycopeptideatlasapi.biomarkercenter.org/api/get-nglycans-count/')
    // fetch('http://localhost:8000/api/get-nglycans-count/')
      .then(response => response.json())
      .then(data => {
        setNGlycansCount(data.count);  // Assuming data is the value you want to display
      })
      .catch(error => console.error('Failed to fetch data:', error));
  }, []);

  const [nGlycositesCount, setNGlycositesCount] = useState(null);
  useEffect(() => {
    fetch('http://glycopeptideatlas-eb-env.eba-zxebtsmp.us-west-2.elasticbeanstalk.com/api/get-nglycosites-count/')
    // fetch('http://localhost:8000/api/get-nglycosites-count/')
      .then(response => response.json())
      .then(data => {
        setNGlycositesCount(data.count);  // Assuming data is the value you want to display
      })
      .catch(error => console.error('Failed to fetch data:', error));
  }, []);



  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* <Heading as="h1" size="2xl" mb={2}>
        GlycopeptideAtlas
      </Heading>
      <Text fontSize="md" mb={4}>
        An active glycoproteomic database for identification of glycosylation changes associated with cancer phenotypes
      </Text> */}
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
        gap='20px'
        mb='20px'>
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdAppRegistration} color={brandColor} />
              }
            />
          }
          name='Projects'
          // value={dataCount}
          value={projectsCount != null ? projectsCount : 'Loading...'}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={PiFilesBold} color={brandColor} />
              }
            />
          }
          name='Files'
          value={filesCount !== null ? filesCount : 'Loading...'}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />
              }
            />
          }
          name='Proteins'
          value={proteinsCount !== null ? proteinsCount : 'Loading...'}
        />

        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdOutlineAccountTree} color={brandColor} />
              }
            />
          }
          name='Glycans'
          value={nGlycansCount !== null ? nGlycansCount : 'Loading...'}
        />


        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='28px' h='28px' as={MdOutlinePassword} color={brandColor} />}
            />
          }
          name='Glycosites'
          value={nGlycositesCount !== null ? nGlycositesCount : 'Loading...'}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdOutlineWater} color={brandColor} />
              }
            />
          }
          name='Glycopeptides'
          value={nglycopeptidesCount !== null ? nglycopeptidesCount : 'Loading...'}
        />

      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px' mb='20px'>
        {/* <TotalSpent /> */}
        <CountCard />
        <WeeklyRevenue />
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 1, xl: 3 }} gap='20px' mb='20px'>
        {/* <CheckTable columnsData={columnsDataCheck} tableData={tableDataCheck} /> */}
        <PieCard gridColumn={{ xl: "span 1" }} />
        <TopGlycansCard gridColumn={{ xl: "span 2" }} />
      </SimpleGrid>
      {/* <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='20px' mb='20px'>
        <ComplexTable
          columnsData={columnsDataComplex}
          tableData={tableDataComplex}
        />
        <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px'>
          <Tasks />
          <MiniCalendar h='100%' minW='100%' selectRange={false} />
        </SimpleGrid>
      </SimpleGrid> */}
    </Box>
  );
}
